import store from "@/store";
import router from "@/router";
import JwtService from "@/common/jwt.service";
import { Auth, Amplify } from "aws-amplify";
import { ADMIN, MERCHANT_ADMIN } from "../services/PermissionsService";
import AmplifyConfigService from "@/services/AmplifyConfigService.js";
import {
  REFRESH_FEATURE_ARRAY,
  CLEAR_FEATURE_ARRAY
} from "@/store/feature.module";

// action types
export const VERIFY_AUTH = "verifyAuth";
export const LOGIN = "login";
export const LOGOUT = "logout";
export const REGISTER = "register";
export const CONFIRM_USER = "confirmUser";
export const GET_GROUPS = "getGroups";
export const GET_TENANT_ALIAS = "getTenantAlias";
export const GET_USER_ATTRIBUTES = "getUserAttributes";
export const COMPLETE_NEW_PASSWORD = "completeNewPassword";
export const SET_GRAPHQL_API_HEADER = "setGraphqlApiHeader";

// mutation types
export const PURGE_AUTH = "logOut";
export const SET_AUTH = "setUser";
export const SET_ERROR = "setError";
export const SET_TIMEOUT = "setTimeout";
export const PURGE_TIMEOUT = "purgeTimeout";
export const SET_USER_ACTION_TAKEN = "setUserActionTaken";

const state = {
  errors: null,
  user: {},
  isAuthenticated: !!JwtService.getToken(),
  inactiveTimeout: null,
  tokenExpirationEpoch: null,
  userActionTaken: false
};

const getters = {
  currentUser(state) {
    return state.user;
  },
  isAuthenticated(state) {
    return state.isAuthenticated;
  },
  merchantId(state, getters) {
    if (!getters.currentUser || !getters.currentUser.attributes) return "";

    return getters.currentUser.attributes["family_name"] ?? "";
  },
  groups(state, getters) {
    if (!getters.currentUser || !getters.currentUser.signInUserSession)
      return "";

    return (
      getters.currentUser.signInUserSession.accessToken.payload[
        "cognito:groups"
      ] ?? []
    );
  },
  isMerchantAdmin(state, getters) {
    if (!getters.groups) {
      return false;
    }
    return getters.groups.includes(MERCHANT_ADMIN);
  },
  isAdmin(state, getters) {
    if (!getters.groups) {
      return false;
    }
    return getters.groups.includes(ADMIN);
  },
  isMerchantUser(state, getters) {
    if (!getters.groups) {
      return false;
    }
    return (
      !getters.isAuthenticated && !getters.isAdmin && !getters.isMerchantAdmin
    );
  },
  userName(state, getters) {
    if (!getters.currentUser || !getters.currentUser.attributes) return "";

    return getters.currentUser.attributes["name"] ?? "";
  },
  userEmail(state, getters) {
    if (!getters.currentUser || !getters.currentUser.attributes) return "";

    return getters.currentUser.attributes["email"] ?? "";
  },
  isUserInactive(state) {
    return !state.userActionTaken;
  },
  tokenExpirationDate(state) {
    return state.tokenExpirationEpoch;
  }
};

const actions = {
  [LOGIN](context, credentials) {
    return new Promise(async (resolve, reject) => {
      Auth.signIn(credentials.email, credentials.password)
        .then(data => {
          handleNewToken(context, data);
          resolve(data);
        })
        .catch(response => {
          context.commit(SET_ERROR, response.message);
          context.dispatch(SET_GRAPHQL_API_HEADER);
          reject(response);
        });
    });
  },
  [LOGOUT](context) {
    return new Promise((resolve, reject) => {
      Auth.signOut()
        .then(() => {
          context.commit(PURGE_AUTH);
          store.dispatch(CLEAR_FEATURE_ARRAY);
          context.dispatch(SET_GRAPHQL_API_HEADER);
          resolve(true);
        })
        .catch(e => {
          context.dispatch(SET_GRAPHQL_API_HEADER);
          reject(e);
        });
    });
  },
  [REGISTER](context, credentials) {
    return new Promise((resolve, reject) => {
      Auth.signUp({
        username: credentials.email,
        password: credentials.password,
        attributes: {
          name: credentials.name
        }
      })
        .then(data => {
          handleNewToken(context, data);
          resolve(data);
        })
        .catch(response => {
          context.commit(SET_ERROR, response.message);
          context.dispatch(SET_GRAPHQL_API_HEADER);
          reject(response);
        });
    });
  },
  [CONFIRM_USER](context, credentials) {
    return new Promise(async (resolve, reject) => {
      await Auth.confirmSignUp(credentials.username, credentials.code)
        .then(data => {
          context.dispatch(SET_GRAPHQL_API_HEADER);
          resolve(data);
        })
        .catch(response => {
          context.commit(SET_ERROR, response.message);
          context.dispatch(SET_GRAPHQL_API_HEADER);
          reject(response);
        });
    });
  },
  [VERIFY_AUTH](context) {
    Auth.currentAuthenticatedUser({
      bypassCache: false
    })
      .then(user => {
        handleNewToken(context, user);
      })
      .catch(response => {
        context.commit(SET_ERROR, response);
        context.dispatch(SET_GRAPHQL_API_HEADER);
      });
  },
  [GET_GROUPS]() {
    return new Promise(async (resolve, reject) => {
      await Auth.currentAuthenticatedUser()
        .then(user => {
          const groups =
            user.signInUserSession.accessToken.payload["cognito:groups"] ?? [];
          return resolve(groups);
        })
        .catch(response => {
          resolve([]);
        });
    });
  },
  [GET_TENANT_ALIAS]() {
    return new Promise(async (resolve, reject) => {
      await Auth.currentAuthenticatedUser()
        .then(user => {
          if (!user || !user.attributes) return "";
          const tenantAlias = user.attributes["family_name"] ?? "";
          return resolve(tenantAlias);
        })
        .catch(response => {
          resolve("");
        });
    });
  },
  [GET_USER_ATTRIBUTES]() {
    return new Promise(async resolve => {
      let user = await Auth.currentAuthenticatedUser();
      const { attributes } = user;
      return resolve(attributes);
    });
  },
  [COMPLETE_NEW_PASSWORD](context, password) {
    return new Promise(async (resolve, reject) => {
      try {
        const user = context.getters.currentUser;
        if (user.challengeName !== "NEW_PASSWORD_REQUIRED")
          throw new Error("Please return to login page and try again");
        const data = await Auth.completeNewPassword(user, password);
        resolve(data);
      } catch (err) {
        if (!!err.message) context.commit(SET_ERROR, err.message);
        reject(err);
      }
    });
  },
  [SET_GRAPHQL_API_HEADER]() {
    const getConfigPromise = new Promise(async resolve => {
      try {
        const currentSession = await Auth.currentSession();
        const token = currentSession.getIdToken().getJwtToken();
        const header = {
          API: {
            graphql_headers: () => ({
              Authorization: token
            })
          }
        };
        resolve(header);
      } catch (e) {
        resolve(null);
      }
    });

    getConfigPromise.then(config =>
      AmplifyConfigService.configureAmplify(Amplify, config)
    );
  }
};

const mutations = {
  [SET_ERROR](state, error) {
    state.errors = error;
  },
  [SET_AUTH](state, user) {
    state.isAuthenticated = true;
    state.user = user;
    state.errors = {};
    JwtService.saveToken(state.user.token);
  },
  [SET_TIMEOUT](state, timerState) {
    clearInactiveTimeout(state.inactiveTimeout);
    state.inactiveTimeout = timerState.timeout;
    state.userActionTaken = false;
    state.tokenExpirationEpoch = timerState.tokenExpirationEpoch;
  },
  [PURGE_TIMEOUT](state) {
    clearInactiveTimeout(state.inactiveTimeout);
    state.inactiveTimeout = null;
    state.userActionTaken = false;
    state.tokenExpirationEpoch = null;
  },
  [PURGE_AUTH](state) {
    state.isAuthenticated = false;
    state.user = {};
    state.errors = {};
    JwtService.destroyToken();
    clearInactiveTimeout(state.inactiveTimeout);
    state.inactiveTimeout = null;
    state.userActionTaken = false;
    state.tokenExpirationEpoch = null;
  },
  [SET_USER_ACTION_TAKEN](state) {
    state.userActionTaken = true;
  }
};

function handleNewToken(context, data) {
  context.commit(SET_AUTH, data);
  context
    .dispatch(SET_GRAPHQL_API_HEADER)
    .then(() => store.dispatch(REFRESH_FEATURE_ARRAY));
  setInactiveTimeout(context, data);
}

function setInactiveTimeout(context, data) {
  try {
    if (!data) {
      context.commit(PURGE_TIMEOUT);
      return;
    }
    const tokenExpirationEpoch = getTokenExpirationEpoch(data);
    if (tokenExpirationEpoch === store.getters.tokenExpirationDate) return;
    const timeDifference = getExpirationTime(tokenExpirationEpoch);
    const timeout = setTimeout(expireSession, timeDifference);
    context.commit(SET_TIMEOUT, { timeout, tokenExpirationEpoch });
    setUserActivityListener();
  } catch (e) {}
}

function getTokenExpirationEpoch(auth) {
  return auth
    .getSignInUserSession()
    .getIdToken()
    .getExpiration();
}

function getExpirationTime(expirationEpoch) {
  const expirationEpochInMiliSeconds = expirationEpoch * 1000;
  const expirationTime = expirationEpochInMiliSeconds - Date.now();

  return !expirationTime || expirationTime < 0 ? 0 : expirationTime;
}

function clearInactiveTimeout(inactiveTimer) {
  if (!inactiveTimer) return;
  clearTimeout(inactiveTimer);
}

function expireSession() {
  if (!store.getters.isAuthenticated) return;
  if (!store.getters.isUserInactive) {
    store.dispatch(VERIFY_AUTH);
    return;
  }
  store.dispatch(LOGOUT).then(() => {
    router.push({ name: "login" });
  });
}

function setUserActivityListener() {
  const actionEvents = [
    "click",
    "mousemove",
    "mousedown",
    "scroll",
    "keypress",
    "touchstart",
    "load"
  ];
  actionEvents.forEach(e => {
    window.removeEventListener(e, setUserAction, { once: true });
    window.addEventListener(e, setUserAction, { once: true });
  });
}

function setUserAction() {
  store.commit(SET_USER_ACTION_TAKEN);
}

export default {
  state,
  actions,
  mutations,
  getters
};
